// Shared, framework-free helpers for showing a policy's assignment/reach — used by the Politikker
// list, its detail drawer, and the Kontroller tab, so the three never disagree on what a number means.

export const CANNOT_FETCH = 'Kan ikke hentes';

export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export function shortDate(iso) {
  return iso ? new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

export const scopeData = (scope) => (scope?.data && !scope.data.error ? scope.data : null);
export const reachOfItem = (item, scope) => scopeData(scope)?.reach?.[`${item.category}:${item.id}`] || null;

// "Alle enheder + 2 grupper (1 undtaget gruppe, med filter)" — with group names when we have them.
export function assignmentText(item, groups) {
  const a = item.assignment;
  if (!a) return CANNOT_FETCH;
  if (a.state === 'none') return 'Ikke tildelt';
  const name = (id) => groups?.[id] || null;
  const parts = [];
  if (a.allDevices) parts.push('Alle enheder');
  if (a.allUsers) parts.push('Alle brugere');
  if (a.includeGroupIds.length) {
    const names = a.includeGroupIds.map(name).filter(Boolean);
    parts.push(names.length === a.includeGroupIds.length && names.length <= 3 ? names.join(', ') : `${a.includeGroupIds.length} ${a.includeGroupIds.length === 1 ? 'gruppe' : 'grupper'}`);
  }
  const extra = [];
  if (a.excludeGroupIds.length) {
    const names = a.excludeGroupIds.map(name).filter(Boolean);
    extra.push(`undtager ${names.length === a.excludeGroupIds.length && names.length <= 3 ? names.join(', ') : `${a.excludeGroupIds.length} ${a.excludeGroupIds.length === 1 ? 'gruppe' : 'grupper'}`}`);
  }
  if (a.filtered) extra.push('med filter');
  return extra.length ? `${parts.join(' + ')} (${extra.join(', ')})` : parts.join(' + ');
}

// What a policy reaches, for the OPENED policy: the full picture (Intune's own count, out of the
// platform's total). `pct` (0-100) is only set when there is a real "of" to divide by.
export function reachCell(item, scope, groups) {
  if (scope?.loading && !scope?.data) return { head: 'Henter…', tone: 'neutral' };
  const data = scopeData(scope);
  if (!data) return { head: CANNOT_FETCH, tone: 'neutral' };
  const r = reachOfItem(item, scope);
  if (!r) return { head: CANNOT_FETCH, tone: 'neutral' };
  if (r.state === 'none') return { head: 'Rammer ingen', sub: 'Politikken er ikke tildelt', tone: 'warn' };
  if (r.state === 'not-device') return { head: assignmentText(item, groups), tone: 'neutral', plain: true };
  if (r.state === 'unknown') {
    const gs = (item.assignment?.includeGroupIds || []).map((id) => data.groups?.[id]);
    let sub = null;
    if (gs.length > 0 && gs.every((g) => g?.readable)) {
      const users = gs.reduce((n, g) => n + g.userCount, 0);
      const devs = gs.reduce((n, g) => n + g.deviceCount, 0);
      sub = ['Grupperne har', [devs ? plural(devs, 'enhed', 'enheder') : null, users ? plural(users, 'bruger', 'brugere') : null].filter(Boolean).join(' og ') || 'ingen medlemmer'].join(' ');
    }
    return { head: assignmentText(item, groups), sub, tone: 'neutral', plain: true, noCount: true };
  }
  if (r.of === 0) return { head: `Ingen ${item.platform ? item.platform + '-' : ''}enheder i tenanten`, tone: 'neutral' };
  const platform = item.platform ? `${item.platform}-enheder` : 'enheder';
  return {
    head: r.of === null ? plural(r.applies, 'enhed', 'enheder') : `${r.applies} af ${r.of} ${platform}`,
    sub: r.problems ? `${r.problems} ikke i orden` : null,
    tone: r.applies < r.of || r.problems ? 'warn' : 'ok',
    pct: r.of ? Math.min(100, Math.round((r.applies / r.of) * 100)) : null,
  };
}

// What a policy reaches, for the LIST: one plain fact ("Tildelt: 2 grupper (16 enheder)"), and — only
// when Intune's own count shows a real gap between who is targeted and who is actually reached — a
// short deviation note. Never a percentage bar, never two numbers that do not obviously relate.
export function scopeLine(item, scope, groups) {
  const a = item.assignment;
  if (!a) return { text: CANNOT_FETCH, tone: 'neutral' };
  if (a.state === 'none') return { text: 'Ikke tildelt', tone: 'warn' };
  const data = scopeData(scope);
  const parts = [];
  if (a.allDevices) parts.push('Alle enheder');
  if (a.allUsers) parts.push('Alle brugere');
  if (a.includeGroupIds.length) {
    const names = a.includeGroupIds.map((id) => groups?.[id]).filter(Boolean);
    const label = names.length === a.includeGroupIds.length && names.length <= 2
      ? names.join(', ')
      : `${a.includeGroupIds.length} ${a.includeGroupIds.length === 1 ? 'gruppe' : 'grupper'}`;
    const gs = a.includeGroupIds.map((id) => data?.groups?.[id]);
    let members = null;
    if (gs.length && gs.every((g) => g?.readable)) {
      const devices = gs.reduce((n, g) => n + g.deviceCount, 0);
      const users = gs.reduce((n, g) => n + g.userCount, 0);
      const bits = [devices ? plural(devices, 'enhed', 'enheder') : null, users ? plural(users, 'bruger', 'brugere') : null].filter(Boolean);
      if (bits.length) members = bits.join(' og ');
    }
    parts.push(members ? `${label} (${members})` : label);
  }
  let text = parts.length ? `Tildelt: ${parts.join(' + ')}` : 'Ikke tildelt';
  if (a.excludeGroupIds.length) text += `, undtager ${a.excludeGroupIds.length} ${a.excludeGroupIds.length === 1 ? 'gruppe' : 'grupper'}`;
  if (a.filtered) text += ', med filter';

  const r = reachOfItem(item, scope);
  let deviation = null;
  if (r?.state === 'counted' && r.source === 'intune' && typeof r.of === 'number' && r.of > r.applies) {
    deviation = `${r.of - r.applies} afviger`;
  }
  return { text, deviation, tone: deviation ? 'warn' : 'neutral' };
}

// A control is only "Opfyldt" when a policy with the right content also reaches everyone. When the
// backend says covered but every device policy behind it reaches only some devices, it is "Delvist".
// Where we cannot count who is reached, the backend's word stands.
export function effectiveStatus(control, itemsById, scope) {
  if (control.status !== 'covered') return { status: control.status, why: null };
  const inForce = control.evidence.filter((e) => e.inForce);
  const counted = inForce.map((e) => {
    const item = itemsById.get(e.id);
    const r = item ? reachOfItem(item, scope) : null;
    return r?.state === 'counted' && r.of ? r : null;
  });
  if (inForce.length === 0 || counted.some((r) => r === null)) return { status: 'covered', why: null };
  if (counted.some((r) => r.applies >= r.of)) return { status: 'covered', why: null };
  const best = counted.reduce((a, b) => (b.applies / b.of > a.applies / a.of ? b : a));
  return { status: 'partial', why: `Bedste politik rammer ${best.applies} af ${best.of} enheder.` };
}

export function itemsByIdFrom(inventoryData) {
  const map = new Map();
  Object.values(inventoryData?.categories || {}).forEach((c) => c.items.forEach((i) => map.set(i.id, i)));
  return map;
}
